import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, MapPin, Phone, ChevronLeft, ChevronRight } from 'lucide-react';

// Reusable Image Slider Component
const ImageSlider = ({ images, autoPlayInterval = 4000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, autoPlayInterval);
    return () => clearInterval(interval);
  }, [images.length, autoPlayInterval]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className="relative w-full rounded-xl overflow-hidden shadow-lg bg-gray-100">
      {/* Main Image Container */}
      <div className="relative flex items-center justify-center min-h-[300px] md:min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt={`Slide ${currentIndex + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-full max-h-[500px] w-auto h-auto object-contain"
            style={{ display: 'block' }}
          />
        </AnimatePresence>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-all shadow-md"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-all shadow-md"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 py-4 bg-white">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex 
                ? 'w-8 h-2 bg-primary' 
                : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const DharamshalasPage = () => {
  // Ramdevra images (4 images)
  const ramdevraImages = [
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/21b9opi7_pic1.jpg',
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/biawnj6r_pic2.jpg',
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/fwcuuzbn_pic3.jpg',
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/a85wgigj_pic4.jpg'
  ];

  // Haridwar images (2 images)
  const haridwarImages = [
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/pnvcr4ui_pic5.jpg',
    'https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/x9rs51th_pic6.jpg'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Dharamshalas</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              धर्मशालाएं
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Dharamshalas - Community Guest Houses at Pilgrimage Sites
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* ========== RAMDEVRA SECTION ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">खत्री धर्मशाला, रामदेवरा</h2>
              <p className="text-gray-500">Khatri Dharamshala, Ramdevra</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-orange-700 mb-4">अखिल भारतीय ब्रह्मक्षत्रिय धर्मशाला ट्रस्ट रामदेवरा</h3>

          <div className="text-gray-700 text-lg leading-relaxed space-y-4 mb-8">
            <p>
              रामदेवरा मंदिर राजस्थान के जैसलमेर जिले में एक प्रमुख धार्मिक स्थल है, जो लोकदेवता बाबा रामदेवजी की समाधि पर बना है। भाद्रपद मास में यहां मेला भरता है, जिसमें विभिन्न प्रांतों के लाखों लोग शामिल होते हैं। अब तो बाबा के दर्शनार्थियों व भक्तों का साल भर ही मेला ही रहता है।
            </p>
            <p>
              ऐसे धार्मिक स्थल पर अखिल भारतीय ब्रह्मक्षत्रिय धर्मशाला ट्रस्ट रामदेवरा द्वारा आधुनिक सुविधाओं युक्त भव्य धर्मशाला का निर्माण करवाया गया है, जिसका उद्घाटन जून, 2025 में एक भव्य समारोह में हुआ। धर्मशाला परिसर में ही मां श्री हिंगलाज का मंदिर भी प्रतिष्ठित किया गया है।
            </p>
            <p>
              <strong>धर्मशाला में आधुनिक सुविधाओं युक्त वातानुकूलित कमरे 50, हॉल, लिफ्ट, बड़ा रसोईघर, समारोह के लिये बड़ा स्थान व पार्किंग सुविधा उपलब्ध है।</strong> यात्री प्रवास के अतिरिक्त विवाह व अन्य समारोहों के लिये यह बहुत उपयुक्त व उपयोगी स्थल है। रामदेवरा में और भी अनेक धर्मशालायें हैं, लेकिन इस खत्री धर्मशाला को श्रेष्ठतम व भव्यतम आंका गया है।
            </p>
            <p>
              धर्मशाला के लिये भूमी आबंटित कराने व उस पर विशाल कॉम्पलेक्स बनाने में बहुत समय, धन व श्रम का त्याग व तपस्यापूर्वक योगदान दिया गया है। इस भगीरथ प्रयास को धरातल पर उतारने वाले समाज बंधुओं का बहुत-बहुत साधुवाद।
            </p>
          </div>
        </motion.div>

        {/* Ramdevra Image Slider */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <ImageSlider images={ramdevraImages} />
        </motion.div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-12"></div>

        {/* ========== HARIDWAR SECTION ========== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">हिंगलाज भवन, हरिद्वार</h2>
              <p className="text-gray-500">Hinglaj Bhawan, Haridwar</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-blue-700 mb-4">अखिल भारतीय मारवाड़ी खत्री समाज सेवा समिति, हरिद्वार</h3>

          <div className="text-gray-700 text-lg leading-relaxed space-y-4 mb-6">
            <p>
              हरिद्वार हिंदू धर्म में एक प्रमुख तीर्थस्थल है, जो गंगा नदी के प्रवेश द्वार के रूप में जाना जाता है। यह पापों का नाश करने व मोक्ष प्राप्ति का प्रतीक माना जाता है। राजा भगीरथ ने गंगा को पृथ्वी पर लाकर अपने पूर्वजों का उद्धार किया था। आज भी हिंदू धर्मावलंबी अपने मृत संबंधियों के पिंडदान व अस्थि विसर्जन के लिये प्रतिदिन हजारों की संख्या में आते हैं।
            </p>
            <p>
              हमारे समाज बंधु भी इस प्रयोजन व तीर्थाटन के उद्देश्य से आते हैं। हरिद्वार में अनेकानेक धर्मशालायें व आश्रम हैं। हमारे समाज की धर्मशाला भी हरिद्वार में हो, यह आवश्यकता महसूस की जाती रही थी। इसकी पूर्ती की है अखिल भारतीय मारवाड़ी खत्री समाज सेवा समिति ने हिंगलाज भवन नाम से धर्मशाला बनाकर। धर्मशाला में 45 वातानुकूलित कमरों मय स्नानघर व भोजनशाला की उत्तम व्यवस्था उपलब्ध है। धर्मशाला का पता है:
            </p>
          </div>

          {/* Contact Box */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white mb-6">
            <p className="font-bold text-lg mb-1">हिंगलाज भवन</p>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-5 h-5 flex-shrink-0" />
              <span>खड़खड़ी रेलवे फाटक के पास, भीमगौड़ा, हरिद्वार, उत्तराखंड</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 flex-shrink-0" />
              <span>सम्पर्क नम्बर : 8107106489, 8003706489</span>
            </div>
          </div>

          <div className="text-gray-700 text-lg leading-relaxed space-y-4 mb-8">
            <p>
              हरिद्वार मंदिरों की नगरी है। हमारी धर्मशाला में भी कुलदेवी श्री हिंगलाज का मंदिर स्थापित है।
            </p>
            <p>
              हरिद्वार में धर्मशाला बहुत-बहुत उपयोगी सिद्ध हो रही है। इसे स्थापित करने व सुचारू रूप से चलाने वाले हमारे बंधुओं का सादर अभिनंदन।
            </p>
          </div>
        </motion.div>

        {/* Haridwar Image Slider */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <ImageSlider images={haridwarImages} />
        </motion.div>

      </div>
    </div>
  );
};

export default DharamshalasPage;
